"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  columnNames,
  DatasetInput,
  useDatasetInput,
} from "@/components/tools/stats-regression/dataset-input";
import { RegressionResults } from "@/components/tools/stats-regression/regression-results";
import { useModelVariables } from "@/components/tools/stats-regression/use-model-variables";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { Dataset } from "@/lib/tool-kit/stats-regression/dataset";
import {
  buildDesign,
  codingOf,
  type Design,
} from "@/lib/tool-kit/stats-regression/design";
import { olsReport } from "@/lib/tool-kit/stats-regression/ols";

/** English strings. A translated content file overrides these through `ui`. */
export const MULTIPLE_REGRESSION_CALCULATOR_STRINGS = {
  dataLabel: "Data — one row per case, one column per variable",
  dataHint:
    "Paste from a spreadsheet (with a header row of variable names), or separate values with tabs, commas, semicolons or spaces. Blank cells and NA count as missing. A text column with two values (yes/no, male/female) works as a 0/1 predictor.",
  placeholder: "mpg\twt\thp\n21\t2.62\t110\n22.8\t2.32\t93",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  readSummary: "Read {rows} × {columns}.",
  rowCount: "one: {count} row|other: {count} rows",
  columnCount: "one: {count} column|other: {count} columns",
  moreRows: "one: … and {count} more row|other: … and {count} more rows",
  missingCells:
    "one: {count} cell is blank or NA.|other: {count} cells are blank or NA.",
  variableName: "Variable {number}",
  example:
    "mpg\twt\thp\tdisp\n21\t2.62\t110\t160\n21\t2.875\t110\t160\n22.8\t2.32\t93\t108\n21.4\t3.215\t110\t258\n18.7\t3.44\t175\t360\n18.1\t3.46\t105\t225\n14.3\t3.57\t245\t360\n24.4\t3.19\t62\t146.7\n22.8\t3.15\t95\t140.8\n19.2\t3.44\t123\t167.6\n17.8\t3.44\t123\t167.6\n16.4\t4.07\t180\t275.8\n17.3\t3.73\t180\t275.8\n15.2\t3.78\t180\t275.8\n10.4\t5.25\t205\t472\n10.4\t5.424\t215\t460\n14.7\t5.345\t230\t440\n32.4\t2.2\t66\t78.7\n30.4\t1.615\t52\t75.7\n33.9\t1.835\t65\t71.1\n21.5\t2.465\t97\t120.1\n15.5\t3.52\t150\t318\n15.2\t3.435\t150\t304\n13.3\t3.84\t245\t350\n19.2\t3.845\t175\t400\n27.3\t1.935\t66\t79\n26\t2.14\t91\t120.3\n30.4\t1.513\t113\t95.1\n15.8\t3.17\t264\t351\n19.7\t2.77\t175\t145\n15\t3.57\t335\t301\n21.4\t2.78\t109\t121",
  responseLabel: "Response (Y) — the variable to predict",
  predictorsLabel: "Predictors (X)",
  levelLabel: "Confidence level",
  level90: "90%",
  level95: "95%",
  level99: "99%",
  binaryTerm: "{name} [{level}]",
  needResponse:
    "Paste data with at least one numeric column to use as the response.",
  needPredictors: "Tick at least one predictor.",
  tooFewCases:
    "Not enough complete cases: with {k} predictors you need at least {needed} rows that have a value for every selected variable.",
  cannotFit:
    "A regression cannot be fitted to these data: the response does not vary in the cases that have every value.",
  droppedRows:
    "one: {count} row with a missing value was left out (listwise deletion).|other: {count} rows with missing values were left out (listwise deletion).",
  results: "Results",
  rSquared: "R²",
  adjRSquared: "Adjusted R²",
  fTest: "F({df1}, {df2})",
  fPValue: "p-value of F",
  residualSe: "Residual standard error",
  casesN: "Cases used (n)",
  equationTitle: "Fitted equation",
  equationIntercept: "ŷ = {a}",
  equationPlus: " + {b} × {name}",
  equationMinus: " − {b} × {name}",
  summary:
    "The model explains {pct} of the variance in {y} (adjusted R² = {adj}). It is {verdict} better than predicting the mean of {y} for every case (F test p {p}).",
  verdictSignificantly: "significantly",
  verdictNotSignificantly: "not significantly",
  coefficientsTitle: "Coefficients",
  colTerm: "Term",
  colB: "B",
  colSe: "SE B",
  colBeta: "β",
  colT: "t",
  colP: "p",
  colCi: "{level} CI for B",
  colVif: "VIF",
  termIntercept: "Intercept",
  ciTemplate: "{low} to {high}",
  aliasedTerm: "aliased",
  aliasedNote:
    "{names}: aliased — it is an exact linear combination of the other predictors, so it adds no information and no coefficient can be estimated. It is left out of the fit, as R's lm does.",
  betaNote:
    "β is the standardized coefficient: the change in Y, in standard deviations, per one standard deviation of the predictor. Compare predictors by β, not B, when their units differ.",
  vifSerious:
    "A VIF above 10 ({names}) signals serious collinearity: the coefficients of overlapping predictors are unstable and their standard errors inflated.",
  vifModerate:
    "A VIF between 5 and 10 ({names}) signals moderate collinearity. The model predicts as well as ever, but the individual coefficients are less certain.",
  namesSeparator: ", ",
  apaTitle: "APA-style regression table",
  apaCopy: "Copy table",
  copyResult: "Copy result",
  copied: "Copied!",
  apaSummary: "R² = {r2}, adjusted R² = {adj}, F({df1}, {df2}) = {f}, p {p}",
  anovaTitle: "Analysis of variance",
  colSource: "Source",
  colDf: "df",
  colSs: "Sum of squares",
  colMs: "Mean square",
  colF: "F",
  sourceRegression: "Regression",
  sourceResidual: "Residual",
  sourceTotal: "Total",
  fitCriteriaTitle: "Model fit",
  colCriterion: "Criterion",
  colValue: "Value",
  criterionAic: "AIC",
  criterionBic: "BIC",
  criterionNote:
    "Lower AIC and BIC mean a better fit for the number of parameters. They are only comparable between models fitted to exactly the same cases.",
  observedPlotTitle: "Observed against predicted",
  observedPlotLabel:
    "Scatter plot of observed against predicted values for {n} cases",
  axisPredicted: "Predicted ŷ",
  axisObserved: "Observed y",
  residualPlotTitle: "Residuals against predicted",
  residualPlotLabel:
    "Scatter plot of residuals against predicted values for {n} cases",
  axisResidual: "Residual (y − ŷ)",
  seriesCases: "Cases",
  seriesZero: "Zero residual",
  plotNote:
    "Look for a curve (a straight-line model is wrong), a funnel (unequal spread), or a lone point far from the rest.",
  casesTitle: "Cases worth a second look",
  casesNone:
    "No case has a standardized residual beyond ±2, a Cook's distance above 4 ÷ n, or high leverage.",
  casesNote:
    "Flags: large residual (|standardized| > 2), influential (Cook's D > 4 ÷ n), high leverage (hat value > 2 × (k + 1) ÷ n). They are prompts to check the data, not reasons to delete a case.",
  colCase: "Case",
  colObserved: "y",
  colFitted: "ŷ",
  colResidual: "Residual",
  colStdResidual: "Std. residual",
  colLeverage: "Leverage",
  colCooks: "Cook's D",
  colFlags: "Flags",
  flagResidual: "large residual",
  flagInfluential: "influential",
  flagLeverage: "high leverage",
  flagSeparator: ", ",
  predictTitle: "Predict y for new values",
  predictHint:
    "Enter a value for every predictor. A two-level predictor is 1 for {level}.",
  predictedY: "Predicted ŷ",
  confidenceInterval: "{level} CI for the mean of y",
  predictionInterval: "{level} prediction interval",
  extrapolation:
    "These values lie outside the combination of predictors seen in your data (leverage {h} against a largest observed {max}). That is extrapolation: treat the prediction with caution.",
  tryExample: "Fill with the means",
  workingTitle: "How it was calculated",
  workFit:
    "Least squares by QR decomposition: b = argmin Σ(y − Xb)², with X holding a column of 1s and the {k} predictors.",
  workR2: "R² = 1 − SSE ÷ SST = 1 − {sse} ÷ {sst} = {r2}",
  workAdj:
    "Adjusted R² = 1 − (1 − R²)(n − 1) ÷ (n − k − 1) = 1 − {oneMinus} × {nm1} ÷ {df} = {adj}",
  workF:
    "F = (R² ÷ k) ÷ ((1 − R²) ÷ (n − k − 1)) = ({r2} ÷ {k}) ÷ ({oneMinus} ÷ {df}) = {f}",
  workSigma:
    "s = √(SSE ÷ (n − k − 1)) = √({sse} ÷ {df}) = {sigma}; SE(bⱼ) = s × √[(XᵀX)⁻¹]ⱼⱼ",
  assumptions:
    "The p-values and intervals assume linearity, independent errors with constant spread, and roughly normal residuals. Check the residual plots, and note that more predictors raise R² even when they add nothing — use adjusted R².",
} as const;

export type RegressionStrings = typeof MULTIPLE_REGRESSION_CALCULATOR_STRINGS;

const LEVELS = { "0.9": 0.9, "0.95": 0.95, "0.99": 0.99 } as const;
type LevelKey = keyof typeof LEVELS;

export default function MultipleRegressionCalculatorWidget() {
  const strings = useToolUi(MULTIPLE_REGRESSION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const input = useDatasetInput();
  const [levelKey, setLevelKey] = useState<LevelKey>("0.95");
  const { dataset } = input;
  const names = columnNames(dataset, strings.variableName);
  const vars = useModelVariables(dataset, (column) => column.isNumeric);

  const termNames = vars.predictors.map((i) => {
    const positive = codingOf(dataset.columns[i]).positive;
    return positive === null
      ? names[i]
      : t(strings.binaryTerm, { name: names[i], level: positive });
  });
  const fit = fitModel(
    dataset,
    vars.yIndex,
    vars.predictors,
    termNames,
    LEVELS[levelKey]
  );
  const options = vars.responses.map((i) => ({
    value: String(i),
    label: names[i],
  }));

  return (
    <>
      <DatasetInput
        example={strings.example}
        fmt={fmt}
        id="mr-data"
        labels={strings}
        locale={locale}
        state={input}
      />

      {vars.yIndex >= 0 ? (
        <section className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="mr-response"
              label={strings.responseLabel}
              onChange={vars.setYChoice}
              options={options}
              value={String(vars.yIndex)}
            />
            <SelectField
              id="mr-level"
              label={strings.levelLabel}
              onChange={setLevelKey}
              options={[
                { value: "0.9", label: strings.level90 },
                { value: "0.95", label: strings.level95 },
                { value: "0.99", label: strings.level99 },
              ]}
              value={levelKey}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className={LABEL}>{strings.predictorsLabel}</legend>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {vars.candidates.map((i) => (
                <Checkbox
                  checked={vars.predictors.includes(i)}
                  key={i}
                  label={names[i]}
                  onChange={() => vars.toggle(i)}
                />
              ))}
            </div>
          </fieldset>
          {fit.design && fit.design.dropped > 0 ? (
            <Notice>
              {tPlural(strings.droppedRows, locale, fit.design.dropped, {
                count: fmt(fit.design.dropped),
              }).trim()}
            </Notice>
          ) : null}
        </section>
      ) : null}

      {dataset.columns.length > 0 && vars.yIndex < 0 ? (
        <Notice>{strings.needResponse}</Notice>
      ) : null}
      {vars.yIndex >= 0 && vars.predictors.length === 0 ? (
        <Notice>{strings.needPredictors}</Notice>
      ) : null}
      {fit.problem ? (
        <Notice>
          {fit.problem === "few"
            ? t(strings.tooFewCases, {
                k: fmt(vars.predictors.length),
                needed: fmt(vars.predictors.length + 2),
              })
            : strings.cannotFit}
        </Notice>
      ) : null}

      {fit.report && fit.design ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <RegressionResults
            design={fit.design}
            fmt={fmt}
            report={fit.report}
            responseName={names[vars.yIndex]}
            strings={strings}
            termNames={termNames}
          />
        </section>
      ) : null}
    </>
  );
}

function designFor(
  dataset: Dataset,
  yIndex: number,
  indexes: number[]
): Design | null {
  if (yIndex < 0 || indexes.length === 0) {
    return null;
  }
  return buildDesign(
    dataset.columns[yIndex].numbers,
    indexes.map((i) => dataset.columns[i])
  );
}

function fitModel(
  dataset: Dataset,
  yIndex: number,
  predictors: number[],
  termNames: string[],
  level: number
) {
  const built = designFor(dataset, yIndex, predictors);
  if (built === null) {
    return { design: null, report: null, problem: null };
  }
  const report = olsReport(termNames, built.predictors, built.y, level);
  let problem: "few" | "flat" | null = null;
  if (report === null) {
    problem = built.y.length < predictors.length + 2 ? "few" : "flat";
  }
  return { design: built, report, problem };
}
